import { Injectable } from '@angular/core';

export interface JwtPayload {
  uid: number;
  nome: string;
  cpf_cnpj: string;
  email: string;
  termoDeUso: number;
  foto: string;
  celular: string;
  telefone1: string;
  telefone2: string;
  status: number;
  listaCodSistemaGestao: string;
  listaSiglaSistemaGestao: string;
  razaoSocial: string;
  perfis: string;
  transacoes: string;
  dataLogin: number;
  iat?: number;
  exp?: number;
}

@Injectable()
export class JwtDecoderService {

  /**
   * Decodifica um JWT sem validar a assinatura (apenas para leitura do payload)
   * @param token JWT token
   * @returns Payload decodificado ou null se inválido
   */
  decodeToken(token: string): JwtPayload | null {
    if (!token) return null;

    try {
      // JWT tem 3 partes separadas por pontos: header.payload.signature
      const parts = token.split('.');
      
      if (parts.length !== 3) {
        console.warn('Token JWT inválido: deve ter 3 partes');
        return null;
      }

      // Decodifica o payload (segunda parte)
      const payload = parts[1];
      
      // Adiciona padding se necessário para Base64
      const paddedPayload = this.addBase64Padding(payload);
      
      // Decodifica de Base64 URL para string
      const decodedString = this.base64UrlDecode(paddedPayload);
      
      // Parse do JSON
      const parsed = JSON.parse(decodedString);
      
      return parsed as JwtPayload;
      
    } catch (error) {
      console.warn('Erro ao decodificar token JWT:');
      return null;
    }
  }

  /**
   * Verifica se o token está expirado
   * @param token JWT token
   * @returns true se expirado, false caso contrário
   */
  isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);
    
    if (!payload || !payload.exp) {
      return true; // Se não conseguir decodificar ou não tem expiração, considera expirado
    }

    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  }

  /**
   * Obtém informações do usuário a partir do token
   * @param token JWT token
   * @returns Dados do usuário ou null
   */
  getUserFromToken(token: string): any {
    const payload = this.decodeToken(token);
    
    if (!payload) return null;

    return {
      uid: payload.uid,
      nome: payload.nome,
      cpf_cnpj: payload.cpf_cnpj,
      email: payload.email,
      termoDeUso: payload.termoDeUso,
      foto: payload.foto,
      celular: payload.celular,
      telefone1: payload.telefone1,
      telefone2: payload.telefone2,
      status: payload.status,
      listaCodSistemaGestao: payload.listaCodSistemaGestao,
      listaSiglaSistemaGestao: payload.listaSiglaSistemaGestao,
      razaoSocial: payload.razaoSocial,
      perfis: payload.perfis,
      transacoes: payload.transacoes,
      dataLogin: payload.dataLogin.toString() || payload.iat.toString()
    };
  }

  /**
   * Decodifica Base64 URL (usado pelo JWT) - APENAS BROWSER
   * @param str String codificada em Base64 URL
   * @returns String decodificada
   */
  private base64UrlDecode(str: string): string {
    // Converte Base64 URL para Base64 padrão
    str = str.replace(/-/g, '+').replace(/_/g, '/');
    
    // Adiciona padding se necessário
    str = this.addBase64Padding(str);
    
    try {
      // Decodifica Base64 para string usando apenas APIs do browser
      const decoded = atob(str);
      
      // Converte para UTF-8 usando escape/unescape (compatível com browsers antigos)
      return decodeURIComponent(escape(decoded));
      
    } catch (error) {
      // Fallback para método mais lento mas compatível
      try {
        const decoded = atob(str);
        return decodeURIComponent(
          decoded
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
      } catch (fallbackError) {
        throw new Error('Erro ao decodificar Base64: ' + error.message);
      }
    }
  }

  /**
   * Adiciona padding necessário para Base64
   * @param str String Base64
   * @returns String com padding correto
   */
  private addBase64Padding(str: string): string {
    const missingPadding = str.length % 4;
    if (missingPadding) {
      str += '='.repeat(4 - missingPadding);
    }
    return str;
  }

  /**
   * Valida se uma string é um JWT válido estruturalmente
   * @param token Token para validar
   * @returns true se tem estrutura de JWT válida
   */
  isValidJwtStructure(token: string): boolean {
    if (!token || typeof token !== 'string') return false;
    
    const parts = token.split('.');
    return parts.length === 3 && 
           parts.every(part => part.length > 0);
  }

  /**
   * Obtém o tempo de expiração do token em formato legível
   * @param token JWT token
   * @returns Data de expiração ou null
   */
  getTokenExpiration(token: string): Date | null {
    const payload = this.decodeToken(token);
    
    if (!payload || !payload.exp) return null;
    
    return new Date(payload.exp * 1000);
  }

  /**
   * Obtém o tempo restante até a expiração em segundos
   * @param token JWT token
   * @returns Segundos até expiração ou 0 se já expirado
   */
  getTimeUntilExpiration(token: string): number {
    const payload = this.decodeToken(token);
    
    if (!payload || !payload.exp) return 0;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const timeLeft = payload.exp - currentTime;
    
    return Math.max(0, timeLeft);
  }
}


// WEBPACK FOOTER //
// /app/src/app/services/jwt-decoder.service.ts